import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { PageSpinner } from "../../ui/Spinner";

/**
 * Analytics · aperçus des dashboards PostHog rendus CÔTÉ SERVEUR (Chrome
 * headless via l'API) puis servis en PNG authentifié. Aucune iframe : les
 * embeds PostHog sont bloqués par les adblockers et par la session PostHog
 * du navigateur de l'admin, l'image passe partout. Clic = ouvre le
 * dashboard interactif dans PostHog. Rafraîchi toutes les 10 minutes.
 */

type DashKey = "site" | "app";

const DASHBOARDS: Array<{
  key: DashKey;
  tab: string;
  title: string;
  desc: string;
  posthogUrl: string;
}> = [
  {
    key: "site",
    tab: "Site vitrine",
    title: "Site vitrine · myvictor.fr",
    desc: "Trafic, pages vues, funnel de souscription et leads démo.",
    posthogUrl: "https://eu.posthog.com/project/193287/dashboard/742695",
  },
  {
    key: "app",
    tab: "Plateforme",
    title: "Plateforme · usage produit",
    desc: "Activité des agences, fonctionnalités utilisées, événements métier.",
    posthogUrl: "https://eu.posthog.com/project/193287/dashboard/742696",
  },
];

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "";

async function fetchPreview(key: string): Promise<string> {
  const token = window.__victorAdminToken;
  const res = await fetch(
    `${API_BASE}/v1/super-admin/analytics/dashboards/${key}/preview`,
    { headers: token ? { Authorization: `Bearer ${token}` } : {} },
  );
  if (!res.ok) throw new Error(`Aperçu indisponible (${res.status})`);
  return URL.createObjectURL(await res.blob());
}

function relTime(ts: number | undefined): string {
  if (!ts) return "—";
  const s = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (s < 60) return "il y a quelques secondes";
  const m = Math.round(s / 60);
  if (m < 60) return `il y a ${m} min`;
  const h = Math.round(m / 60);
  return `il y a ${h} h`;
}

function DashboardPreview({ dash }: { dash: (typeof DASHBOARDS)[number] }) {
  const previewQ = useQuery({
    queryKey: ["dash-preview", dash.key],
    queryFn: () => fetchPreview(dash.key),
    staleTime: 5 * 60_000,
    refetchInterval: 10 * 60_000,
    retry: 1,
  });

  return (
    <div className="overflow-hidden rounded-2xl bg-white ring-1 ring-navy-100 shadow-sm">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-navy-100 px-5 py-4">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-wider text-navy-400">
            Dashboard PostHog
          </p>
          <h2 className="text-base font-bold text-navy-800">{dash.title}</h2>
          <p className="mt-0.5 text-sm text-navy-500">{dash.desc}</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="hidden text-xs text-navy-400 sm:inline">
            {previewQ.isFetching
              ? "actualisation…"
              : `rafraîchi ${relTime(previewQ.dataUpdatedAt)}`}
          </span>
          <a
            href={dash.posthogUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-accent-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-accent-700"
          >
            Ouvrir
            <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M7 17 17 7M9 7h8v8" />
            </svg>
          </a>
        </div>
      </div>

      {/* Aperçu */}
      {previewQ.isLoading ? (
        <div className="flex h-72 flex-col items-center justify-center gap-3">
          <PageSpinner />
          <p className="text-xs text-navy-500">
            Génération de l'aperçu… (jusqu'à 30 s la première fois)
          </p>
        </div>
      ) : previewQ.error ? (
        <div className="flex h-44 flex-col items-center justify-center gap-2 px-6 text-center">
          <p className="text-sm text-navy-500">Aperçu indisponible pour le moment.</p>
          <a
            href={dash.posthogUrl}
            target="_blank"
            rel="noreferrer"
            className="text-sm font-semibold text-accent-600 hover:underline"
          >
            Ouvrir dans PostHog →
          </a>
        </div>
      ) : (
        <a
          href={dash.posthogUrl}
          target="_blank"
          rel="noreferrer"
          title="Ouvrir le dashboard interactif dans PostHog"
          className="group block bg-navy-50/40 p-3 sm:p-4"
        >
          <img
            src={previewQ.data}
            alt={`Aperçu ${dash.title}`}
            className="w-full rounded-xl ring-1 ring-navy-100 transition-opacity group-hover:opacity-95"
          />
          <p className="mt-2 text-center text-[11px] text-navy-400">
            Cliquez pour ouvrir le dashboard interactif dans PostHog
          </p>
        </a>
      )}
    </div>
  );
}

export function AnalyticsPage() {
  const [active, setActive] = useState<DashKey>("site");
  const dash = DASHBOARDS.find((d) => d.key === active) ?? DASHBOARDS[0]!;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-navy-800 sm:text-3xl">
            Analytics
          </h1>
          <p className="mt-1 text-sm text-navy-600">
            Trafic du site vitrine et usage de la plateforme, via PostHog —
            aperçus rafraîchis toutes les 10 minutes.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a
            href="https://eu.posthog.com/project/193287/max"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2 text-sm font-semibold text-navy-700 ring-1 ring-navy-200 transition-colors hover:bg-navy-50"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 3v3" />
              <path d="M5 10c0-2.2 1.8-4 4-4h6c2.2 0 4 1.8 4 4v5c0 2.2-1.8 4-4 4H9c-2.2 0-4-1.8-4-4v-5Z" />
              <circle cx="9.5" cy="12.5" r="1" />
              <circle cx="14.5" cy="12.5" r="1" />
            </svg>
            Interroger Max
          </a>
          <a
            href="https://eu.posthog.com"
            target="_blank"
            rel="noreferrer"
            className="text-sm font-semibold text-accent-600 hover:underline"
          >
            Ouvrir PostHog →
          </a>
        </div>
      </header>

      {/* Sélecteur de dashboard */}
      <div className="inline-flex rounded-xl bg-white p-1 ring-1 ring-navy-100 shadow-sm">
        {DASHBOARDS.map((d) => {
          const isActive = d.key === active;
          return (
            <button
              key={d.key}
              type="button"
              onClick={() => setActive(d.key)}
              className={`rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${
                isActive
                  ? "bg-accent-600 text-white shadow-sm"
                  : "text-navy-600 hover:bg-navy-100"
              }`}
            >
              {d.tab}
            </button>
          );
        })}
      </div>

      <DashboardPreview key={dash.key} dash={dash} />
    </div>
  );
}
